import React from 'react'
import ChapterCardCompact from './ChapterCardCompact'
import { useBooksStore } from '../../store/booksStore'
import type { Book } from '../../store/booksStore'
import './HomeBookDashboard.css'
import './HomeDashboardShared.css'

interface HomeBookDashboardProps {
    bookId: string
    onChapterClick?: (chapterId: string) => void
    onAddChapter?: () => void
    onAllBooksClick?: () => void
}

const HomeBookDashboard: React.FC<HomeBookDashboardProps> = ({
    bookId,
    onChapterClick,
    onAddChapter,
    onAllBooksClick
}) => {
    const { books, getBookChapters, getChapterWitnesses, createChapter } = useBooksStore()
    const book = books.find(b => b.id === bookId)
    const [isCreating, setIsCreating] = React.useState(false)
    const [newChapterName, setNewChapterName] = React.useState('')
    const [sortBy, setSortBy] = React.useState<'recent' | 'alpha'>('recent')

    if (!book) {
        return (
            <div className="home-book-dashboard">
                <div className="context-header">
                    <div className="context-breadcrumbs">
                        Book Not Found
                    </div>
                </div>
            </div>
        )
    }

    const chapters = getBookChapters(book.id)

    // Sort chapters
    const sortedChapters = [...chapters].sort((a, b) => {
        if (sortBy === 'alpha') {
            return (a.name || '').localeCompare(b.name || '')
        }
        // Recent first - get max timestamp for each chapter
        const getChapterTimestamp = (chapter: typeof chapters[0]) => {
            const witnesses = getChapterWitnesses(chapter.id)
            if (witnesses.length === 0) return chapter.created_at
            const witnessTimestamps = witnesses.map(w => w.last_modified || 0)
            return Math.max(chapter.created_at, ...witnessTimestamps)
        }
        return getChapterTimestamp(b) - getChapterTimestamp(a)
    })

    // Get most recent modification timestamp
    const getLastModified = (): number => {
        if (chapters.length === 0) return book.last_modified || book.created_at

        const chapterTimestamps = chapters.map(chapter => {
            const witnesses = getChapterWitnesses(chapter.id)
            if (witnesses.length === 0) return chapter.created_at

            const witnessTimestamps = witnesses.map(w => w.last_modified || 0)
            return Math.max(chapter.created_at, ...witnessTimestamps)
        })

        return Math.max(book.last_modified || book.created_at, ...chapterTimestamps)
    }

    const lastModified = getLastModified()

    // Format timestamp
    const getTimestampText = (): string => {
        const now = Date.now()
        const diff = now - lastModified

        if (diff < 60000) return 'just now'
        if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`
        if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`
        return `${Math.floor(diff / 86400000)} days ago`
    }

    const handleChapterClick = (chapterId: string) => {
        if (onChapterClick) {
            onChapterClick(chapterId)
        }
    }

    const handleStartCreate = () => {
        setIsCreating(true)
        setNewChapterName('')
        // Focus the input after state updates
        setTimeout(() => {
            const input = document.querySelector('.inline-create-input') as HTMLInputElement
            input?.focus()
        }, 0)
    }

    const handleCancelCreate = () => {
        setIsCreating(false)
        setNewChapterName('')
    }

    const handleSubmitCreate = async () => {
        if (!newChapterName.trim()) return

        if (onAddChapter) {
            onAddChapter()
            return
        }

        try {
            await createChapter(bookId, newChapterName.trim())
            setIsCreating(false)
            setNewChapterName('')
        } catch (error) {
            console.error("Failed to create chapter:", error)
            alert("Failed to create chapter")
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSubmitCreate()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancelCreate()
        }
    }

    return (
        <div className="home-book-dashboard">
            <div className="context-header">
                <div className="context-breadcrumbs">
                    <span
                        className="breadcrumb-clickable"
                        onClick={() => onAllBooksClick?.()}
                    >
                        All Books
                    </span>
                    <span className="breadcrumb-divider">›</span>
                    {book.name}
                </div>
                <div className="context-meta">
                    {chapters.length} {chapters.length === 1 ? 'Chapter' : 'Chapters'} • Last modified {getTimestampText()}
                    <span
                        className="sort-toggle"
                        onClick={() => setSortBy(sortBy === 'recent' ? 'alpha' : 'recent')}
                        title={sortBy === 'recent' ? 'Sort alphabetically' : 'Sort by recent'}
                    >
                        {sortBy === 'recent' ? '↓ Recent' : '↓ A-Z'}
                    </span>
                </div>
            </div>

            <div className="chapter-grid">
                {/* Add New Card or Inline Create Form */}
                {!isCreating ? (
                    <div className="chapter-card-compact card-add-new" onClick={handleStartCreate}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span className="token-label">Add Chapter</span>
                    </div>
                ) : (
                    <div className="chapter-card-compact card-inline-create">
                        <div className="inline-create-form">
                            <input
                                type="text"
                                className="inline-create-input"
                                placeholder="Chapter name..."
                                value={newChapterName}
                                onChange={(e) => setNewChapterName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus
                            />
                            <div className="inline-create-actions">
                                <button
                                    className="inline-create-btn btn-submit"
                                    onClick={handleSubmitCreate}
                                    disabled={!newChapterName.trim()}
                                >
                                    Create
                                </button>
                                <button
                                    className="inline-create-btn btn-cancel"
                                    onClick={handleCancelCreate}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Chapter Cards */}
                {sortedChapters.map((chapter) => (
                    <ChapterCardCompact
                        key={chapter.id}
                        chapter={chapter}
                        bookId={bookId}
                        onClick={() => handleChapterClick(chapter.id)}
                    />
                ))}
            </div>
        </div>
    )
}

export default HomeBookDashboard
